import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type CallbackOtpType = 'signup' | 'invite' | 'magiclink' | 'recovery' | 'email_change' | 'email';

const CALLBACK_OTP_TYPES = new Set<CallbackOtpType>([
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email',
]);

function readOtpType(value: string | null): CallbackOtpType | null {
  return CALLBACK_OTP_TYPES.has(value as CallbackOtpType) ? (value as CallbackOtpType) : null;
}

function readLocalRedirect(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

function readHashSession(hash: string): { access_token: string; refresh_token: string } | null {
  const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
  const accessToken = hashParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token');
  if (!accessToken || !refreshToken) return null;
  return { access_token: accessToken, refresh_token: refreshToken };
}

function postAuthPath(params: URLSearchParams): string {
  const redirect = readLocalRedirect(params.get('redirect'));
  if (redirect) return redirect;

  const intake = params.get('intake');
  const repurchase = params.get('repurchase');
  if (intake) return `/start?intake=${encodeURIComponent(intake)}${repurchase ? '&repurchase=1' : ''}`;

  return '/start';
}

export function AuthCallbackPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const completionRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    async function completeSignIn() {
      const providerError = params.get('error_description') ?? params.get('error');
      if (providerError) throw new Error(providerError);

      const code = params.get('code');
      const tokenHash = params.get('token_hash');
      const otpType = readOtpType(params.get('type'));
      const hashSession = readHashSession(window.location.hash);

      const result = hashSession
        ? await supabase.auth.setSession(hashSession)
        : code
          ? await supabase.auth.exchangeCodeForSession(code)
          : tokenHash && otpType
            ? await supabase.auth.verifyOtp({ token_hash: tokenHash, type: otpType })
            : await supabase.auth.getSession();

      if (result.error) throw result.error;

      const session = result.data.session ?? (await supabase.auth.getSession()).data.session;
      if (!session) {
        throw new Error('We could not finish signing you in. Open the newest magic link in the same browser you used to request it.');
      }

      nav(postAuthPath(params), { replace: true });
    }

    completionRef.current ??= completeSignIn();
    completionRef.current.catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'We could not finish signing you in.');
    });
  }, [nav, params]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#080a0f] p-8 text-white">
        <div className="mx-auto mt-24 max-w-md rounded-lg border border-red-500/25 bg-red-500/10 p-5">
          <h1 className="text-lg font-semibold">Sign-in link did not work</h1>
          <p className="mt-2 text-sm text-slate-300">{error}</p>
          <button
            type="button"
            onClick={() => nav('/login', { replace: true })}
            className="mt-5 rounded-lg bg-[#fe5963] px-4 py-2 text-sm font-semibold text-white hover:bg-[#fe5963]/90"
          >
            Request a new link
          </button>
        </div>
      </div>
    );
  }

  return <div className="p-8 text-graphite">Signing you in...</div>;
}
